import { useEffect, useState, useCallback } from 'react';
import SyncEngine from '../sync/SyncEngine';
import { getDatabase } from '../index';
import { isLocalId } from '../sync/diffUtils';
import CaseRepository from '../repositories/CaseRepository';
import JurorRepository from '../repositories/JurorRepository';

/**
 * useCaseSync - Hook for syncing a case with the server
 * 
 * Handles:
 * - Subscribing to real-time WebSocket updates
 * - Initial data fetch if needed
 * - Catch-up sync on reconnect
 * 
 * @param {string} caseId - Case ID to sync
 * @returns {{ isLoading: boolean, error: Error|null, refetch: Function }}
 */
export function useCaseSync(caseId) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Skip sync entirely for local-only cases (UUID IDs that haven't been synced to server yet)
  const isLocal = caseId && isLocalId(caseId);

  const refetch = useCallback(async () => {
    if (!caseId || isLocal) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      await SyncEngine.performFullSync(caseId);
    } catch (err) {
      console.error('Failed to sync case:', err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [caseId, isLocal]);

  useEffect(() => {
    if (!caseId || isLocal) {
      setIsLoading(false);
      return;
    }

    let mounted = true;
    
    const initSync = async () => {
      try {
        // Subscribe to case updates via WebSocket + perform catch-up sync
        await SyncEngine.subscribeToCase(caseId);
        
        if (mounted) {
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Failed to initialize case sync:', err);
        if (mounted) {
          setError(err);
          setIsLoading(false);
        }
      }
    };

    initSync();

    return () => {
      mounted = false;
      SyncEngine.unsubscribeFromCase(caseId);
    };
  }, [caseId, isLocal]);

  return { isLoading, error, refetch };
}

/**
 * useCaseData - Hook to get case data with sync
 * 
 * Combines sync subscription with case data retrieval.
 * Use this as a drop-in replacement for Redux case selectors.
 * 
 * @param {string} caseId - Case ID
 * @returns {{ caseInfo: Object, jurors: Array, isLoading: boolean, error: Error|null }}
 */
export function useCaseData(caseId) {
  const { isLoading: syncLoading, error: syncError, refetch } = useCaseSync(caseId);
  const [caseInfo, setCaseInfo] = useState(null);
  const [jurors, setJurors] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    if (!caseId) return;

    const db = getDatabase();
    if (!db) {
      setDataLoading(false);
      return;
    }

    let mounted = true;

    // Set up live queries
    const loadData = async () => {
      try {
        // Get case
        const caseData = await CaseRepository.getById(caseId);
        if (mounted) setCaseInfo(caseData);

        // Get jurors
        const jurorsList = await JurorRepository.getForCase(caseId);
        if (mounted) setJurors(jurorsList);

        if (mounted) setDataLoading(false);
      } catch (err) {
        console.error('Failed to load case data:', err);
        if (mounted) setDataLoading(false);
      }
    };

    loadData();

    // Subscribe to changes using Dexie hooks pattern
    // For reactive updates, components should use useLiveQuery directly
    const interval = setInterval(loadData, 1000); // Poll for changes (temp solution)

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [caseId]);

  return {
    caseInfo: caseInfo || {
      _id: caseId,
      name: '',
      jurors: [],
      spacers: [],
      numberOfAlternates: 0,
      revision: 0
    },
    jurors,
    isLoading: syncLoading || dataLoading,
    error: syncError,
    refetch
  };
}

/**
 * useInitialCaseLoad - Hook to load initial case data from server
 * 
 * Fetches case info and populates local database if empty.
 * Should be used at the Case page level.
 * 
 * @param {string} caseId - Case ID
 */
export function useInitialCaseLoad(caseId) {
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    if (!caseId || hasLoaded) return;

    const loadInitialData = async () => {
      const db = getDatabase();
      if (!db) return;

      try {
        // Check if we have the case locally
        const localCase = await db.cases.get(caseId);
        
        if (!localCase) {
          // Need to fetch from server
          console.log('Case not in local DB, performing full sync');
          await SyncEngine.performFullSync(caseId);
        } else {
          // Just do a catch-up sync for any missed changes
          await SyncEngine.performCatchUpSync(caseId);
        }
        
        setHasLoaded(true);
      } catch (err) {
        console.error('Failed to load initial case data:', err);
        setHasLoaded(true); // Mark as loaded to prevent infinite retries
      }
    };

    loadInitialData();
  }, [caseId, hasLoaded]);

  return { hasLoaded };
}

export default {
  useCaseSync,
  useCaseData,
  useInitialCaseLoad
};
